import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Scale,
  Users,
  DollarSign,
  Briefcase,
  TrendingUp,
  AlertCircle,
  Loader2,
  ArrowLeft
} from 'lucide-react';
import AdminLayout from '../../components/admin/AdminLayout';
import { useAdminAuth } from '../../contexts/AdminAuthContext';

const AdminDashboard = () => {
  const navigate = useNavigate();
  const { token, hasPermission } = useAdminAuth();
  // استخدام بيانات افتراضية مؤقتاً
  const [stats] = useState({
    legal: { totalCases: 0, activeCases: 0, urgentCases: 0 },
    crm: { totalClients: 0, individualClients: 0, businessClients: 0 },
    hr: { totalEmployees: 0, activeEmployees: 0, pendingLeaves: 0 },
    alerts: { urgentCases: 0, pendingLeaves: 0 }
  });
  const [loading] = useState(false);

  const tiles = [
    {
      id: 'legal',
      title: 'الإدارة القانونية',
      titleEn: 'Legal Management',
      icon: Scale,
      color: '#1E3A8A',
      path: '/admin/legal/cases',
      permission: 'legal.view',
      stats: stats?.legal,
      items: [
        { label: 'إجمالي القضايا', value: stats?.legal?.totalCases || 0 },
        { label: 'قضايا نشطة', value: stats?.legal?.activeCases || 0 },
        { label: 'قضايا عاجلة', value: stats?.legal?.urgentCases || 0, highlight: true }
      ]
    },
    {
      id: 'crm',
      title: 'إدارة العملاء',
      titleEn: 'CRM',
      icon: Users,
      color: '#1E3A8A',
      path: '/admin/crm/individuals',
      permission: 'crm.view',
      stats: stats?.crm,
      items: [
        { label: 'إجمالي العملاء', value: stats?.crm?.totalClients || 0 },
        { label: 'عملاء الأفراد', value: stats?.crm?.individualClients || 0 },
        { label: 'عملاء الشركات', value: stats?.crm?.businessClients || 0 }
      ]
    },
    {
      id: 'finance',
      title: 'الإدارة المالية',
      titleEn: 'Finance',
      icon: DollarSign,
      color: '#1E3A8A',
      path: '/admin/finance/invoices',
      permission: 'finance.view',
      items: [
        { label: 'قيد التطوير', value: '-' }
      ]
    },
    {
      id: 'hr',
      title: 'الموارد البشرية',
      titleEn: 'HR',
      icon: Briefcase,
      color: '#1E3A8A',
      path: '/admin/hr/employees',
      permission: 'hr.view',
      stats: stats?.hr,
      items: [
        { label: 'إجمالي الموظفين', value: stats?.hr?.totalEmployees || 0 },
        { label: 'موظفين نشطين', value: stats?.hr?.activeEmployees || 0 },
        { label: 'إجازات معلقة', value: stats?.hr?.pendingLeaves || 0, highlight: true }
      ]
    }
  ];

  const visibleTiles = tiles.filter(tile => 
    !tile.permission || hasPermission(tile.permission)
  );

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-full">
          <Loader2 className="w-12 h-12 text-[#1E3A8A] animate-spin" />
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="p-6 max-w-7xl mx-auto">
        {/* Welcome Section */}
        <div className="mb-6 p-6 bg-gradient-to-r from-[#9C8862] via-[#A67C52] to-[#9C8862] rounded-xl shadow-xl border-2 border-[#1A1A1A]">
          <h1 className="text-3xl font-black text-[#1A1A1A] mb-2 drop-shadow-lg">لوحة التحكم الرئيسية</h1>
          <p className="text-[#1A1A1A]/70 font-semibold">نظرة شاملة على منصة وازن القانونية</p>
        </div>

        {/* Alerts */}
        {stats?.alerts && (stats.alerts.urgentCases > 0 || stats.alerts.pendingLeaves > 0) && (
          <div className="mb-6 p-4 bg-orange-50 border-2 border-orange-200 rounded-xl flex items-start gap-3">
            <AlertCircle className="w-6 h-6 text-orange-600 flex-shrink-0" />
            <div className="flex-1">
              <h3 className="font-bold text-orange-900 mb-1">تنبيهات تحتاج انتباهك</h3>
              <ul className="text-sm text-orange-800 space-y-1">
                {stats.alerts.urgentCases > 0 && (
                  <li>• {stats.alerts.urgentCases} قضية عاجلة تحتاج متابعة</li>
                )}
                {stats.alerts.pendingLeaves > 0 && (
                  <li>• {stats.alerts.pendingLeaves} طلب إجازة معلق</li>
                )}
              </ul>
            </div>
          </div>
        )}

        {/* Main Tiles Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
          {visibleTiles.map((tile) => {
            const TileIcon = tile.icon;
            return (
              <div
                key={tile.id}
                onClick={() => navigate(tile.path)}
                className="bg-gradient-to-br from-white to-[#F5F5F5] rounded-xl shadow-xl border-3 border-[#9C8862] p-6 hover:shadow-2xl hover:border-[#A67C52] hover:from-[#9C8862]/10 transition-all cursor-pointer group"
              >
                {/* Header */}
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div 
                      className="w-12 h-12 rounded-lg flex items-center justify-center bg-gradient-to-br from-[#9C8862] via-[#A67C52] to-[#8B7355] shadow-xl border-2 border-[#1A1A1A]"
                    >
                      <TileIcon 
                        className="w-6 h-6 text-white"
                        strokeWidth={3}
                      />
                    </div>
                    <div>
                      <h3 className="text-lg font-black text-[#1A1A1A] group-hover:text-[#9C8862] transition-colors">
                        {tile.title}
                      </h3>
                      <p className="text-xs text-[#9C8862] font-semibold">{tile.titleEn}</p>
                    </div>
                  </div>
                  <ArrowLeft className="w-5 h-5 text-[#9C8862] group-hover:text-[#1A1A1A] transition-colors" strokeWidth={3} />
                </div>

                {/* Stats */}
                <div className="space-y-3">
                  {tile.items.map((item, index) => (
                    <div
                      key={index}
                      className={`flex items-center justify-between p-3 rounded-lg ${
                        item.highlight ? 'bg-orange-50' : 'bg-gray-50'
                      }`}
                    >
                      <span className={`text-sm font-semibold ${
                        item.highlight ? 'text-orange-800' : 'text-gray-700'
                      }`}>
                        {item.label}
                      </span>
                      <span className={`text-xl font-black ${
                        item.highlight ? 'text-orange-600' : 'text-[#9C8862]'
                      }`}>
                        {item.value}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>

        {/* Quick Actions */}
        <div className="bg-gradient-to-br from-white to-[#F5F5F5] rounded-xl shadow-xl border-3 border-[#9C8862] p-6">
          <h2 className="text-xl font-black text-[#1A1A1A] mb-4 flex items-center gap-2">
            <TrendingUp className="w-6 h-6 text-[#9C8862]" strokeWidth={3} />
            <span>إجراءات سريعة</span>
          </h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {hasPermission('legal.view') && (
              <button
                onClick={() => navigate('/admin/legal/cases')}
                className="p-4 border-2 border-gray-200 rounded-lg hover:border-[#C8A870] hover:shadow-md transition-all text-center"
              >
                <Scale className="w-8 h-8 mx-auto mb-2 text-[#1E3A8A]" />
                <p className="text-sm font-bold text-gray-700">إدارة القضايا</p>
              </button>
            )}
            {hasPermission('crm.view') && (
              <button
                onClick={() => navigate('/admin/crm/individuals')}
                className="p-4 border-2 border-gray-200 rounded-lg hover:border-[#C8A870] hover:shadow-md transition-all text-center"
              >
                <Users className="w-8 h-8 mx-auto mb-2 text-[#1E3A8A]" />
                <p className="text-sm font-bold text-gray-700">إدارة العملاء</p>
              </button>
            )}
            {hasPermission('finance.view') && (
              <button
                onClick={() => navigate('/admin/finance/invoices')}
                className="p-4 border-2 border-gray-200 rounded-lg hover:border-[#C8A870] hover:shadow-md transition-all text-center"
              >
                <DollarSign className="w-8 h-8 mx-auto mb-2 text-[#1E3A8A]" />
                <p className="text-sm font-bold text-gray-700">الإدارة المالية</p>
              </button>
            )}
            {hasPermission('hr.view') && (
              <button
                onClick={() => navigate('/admin/hr/employees')}
                className="p-4 border-2 border-gray-200 rounded-lg hover:border-[#C8A870] hover:shadow-md transition-all text-center"
              >
                <Briefcase className="w-8 h-8 mx-auto mb-2 text-[#1E3A8A]" />
                <p className="text-sm font-bold text-gray-700">الموارد البشرية</p>
              </button>
            )}
          </div>
        </div>
      </div>
    </AdminLayout>
  );
};

export default AdminDashboard;